import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Download, X, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_API_URL;

const CATEGORY_ORDER = ['Browser', 'Tools', 'Produktivität', 'Multimedia', 'Entwicklung', 'Kommunikation', 'Gaming'];
const CATEGORY_COLORS = {
    Browser:       '#60a5fa',
    Tools:         '#a78bfa',
    Produktivität: '#2dd4bf',
    Multimedia:    '#f472b6',
    Entwicklung:   '#4ade80',
    Kommunikation: '#fb923c',
    Gaming:        '#facc15',
};

// ── einzelne Programm-Karte ───────────────────────────────────────────
function ProgramCard({ program, isSelected, onToggle }) {
    return (
        <button
            onClick={onToggle}
            style={{
                display: 'flex', flexDirection: 'column', gap: '6px',
                padding: '14px 16px',
                background: isSelected ? 'rgba(251,146,60,0.07)' : 'var(--card-bg)',
                border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
                transition: 'border-color 0.12s, background 0.12s',
                fontFamily: 'inherit', position: 'relative',
            }}
        >
            {/* Checkbox */}
            <div style={{
                position: 'absolute', top: '12px', right: '12px',
                width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0,
                border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                background: isSelected ? 'var(--accent)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.12s',
            }}>
                {isSelected && <Check size={11} color="#000" strokeWidth={3} />}
            </div>

            <div style={{
                fontSize: '13px', fontWeight: 700,
                color: 'var(--text-primary)', paddingRight: '26px', lineHeight: 1.3,
            }}>
                {program.name}
            </div>
            <div style={{
                fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4,
            }}>
                {program.description}
            </div>
        </button>
    );
}

// ── Haupt-Komponente ──────────────────────────────────────────────────
export default function Installer() {
    const [programs, setPrograms]     = useState([]);
    const [selected, setSelected]     = useState(new Set());
    const [search, setSearch]         = useState('');
    const [loading, setLoading]       = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError]           = useState('');
    const [done, setDone]             = useState(false);

    useEffect(() => {
        fetch(`${API}/api/programs`)
            .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
            .then(data => { setPrograms(data); setLoading(false); })
            .catch(e => { setError(`Programme konnten nicht geladen werden: ${e.message}`); setLoading(false); });
    }, []);

    // Filter nach Suchbegriff
    const filtered = useMemo(() => {
        if (!search.trim()) return programs;
        const q = search.toLowerCase();
        return programs.filter(p =>
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q) ||
            p.category.toLowerCase().includes(q)
        );
    }, [programs, search]);

    // Nach Kategorie gruppieren
    const grouped = useMemo(() => {
        const g = {};
        for (const p of filtered) {
            if (!g[p.category]) g[p.category] = [];
            g[p.category].push(p);
        }
        return g;
    }, [filtered]);

    const categories = CATEGORY_ORDER.filter(c => grouped[c]?.length > 0);

    const toggle = useCallback((id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleCategory = useCallback((cat) => {
        const ids = (grouped[cat] || []).map(p => p.id);
        setSelected(prev => {
            const next = new Set(prev);
            const allSel = ids.every(id => next.has(id));
            ids.forEach(id => allSel ? next.delete(id) : next.add(id));
            return next;
        });
    }, [grouped]);

    const clearAll = () => setSelected(new Set());

    const downloadInstaller = async () => {
        if (!selected.size || downloading) return;
        setDownloading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/build`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ program_ids: [...selected] }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                throw new Error(d.detail || 'Build fehlgeschlagen');
            }
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a');
            a.href     = url;
            a.download = 'MakeItSimple-Installer.exe';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setDone(true);
            setTimeout(() => setDone(false), 3000);
        } catch (e) {
            setError(e.message);
        }
        setDownloading(false);
    };

    return (
        <div style={{ minHeight: 'calc(100vh - 70px)', paddingBottom: '88px' }}>
            <div className="container" style={{ paddingTop: '28px' }}>

                {/* Zurück */}
                <Link to="/privat" className="icon-button"
                    style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '28px' }}>
                    <ArrowLeft size={18} />
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>Zurück</span>
                </Link>

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                    <h1 className="section-title" style={{ marginBottom: '6px' }}>MakeItSimple Installer</h1>
                    <p className="section-subtitle" style={{ marginBottom: '24px' }}>
                        Programme auswählen und als fertige .exe herunterladen
                    </p>

                    {/* Info-Banner */}
                    <div style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                        padding: '12px 16px',
                        background: 'rgba(251,146,60,0.06)',
                        border: '1px solid rgba(251,146,60,0.2)',
                        borderRadius: '10px',
                        fontSize: '12px', color: 'var(--text-secondary)',
                        lineHeight: 1.6, marginBottom: '24px',
                    }}>
                        <span style={{ color: 'var(--accent)', fontSize: '16px', flexShrink: 0 }}>ℹ</span>
                        <span>
                            Die heruntergeladene <strong style={{ color: 'var(--accent)' }}>.exe</strong> einfach
                            doppelklicken und die Windows-Abfrage (UAC) mit <em>„Ja"</em> bestätigen – die Programme
                            werden dann automatisch per <strong style={{ color: 'var(--accent)' }}>winget</strong> installiert
                            bzw. aktualisiert. Beim ersten Start kann <strong style={{ color: 'var(--accent)' }}>SmartScreen</strong> warnen
                            („Unbekannter Herausgeber") → <em>Weitere Informationen → Trotzdem ausführen</em>.
                        </span>
                    </div>

                    {/* Suchleiste */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <Search size={15} style={{
                                position: 'absolute', left: '13px', top: '50%',
                                transform: 'translateY(-50%)', color: 'var(--text-secondary)',
                                pointerEvents: 'none',
                            }} />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Programme suchen..."
                                style={{
                                    width: '100%', boxSizing: 'border-box',
                                    paddingLeft: '40px', paddingRight: search ? '36px' : '14px',
                                    paddingTop: '10px', paddingBottom: '10px',
                                    background: 'var(--card-bg)', border: '2px solid var(--border)',
                                    borderRadius: '10px', color: 'var(--text-primary)',
                                    fontFamily: 'inherit', fontSize: '14px', outline: 'none',
                                    transition: 'border-color 0.15s',
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'}
                            />
                            {search && (
                                <button onClick={() => setSearch('')} style={{
                                    position: 'absolute', right: '10px', top: '50%',
                                    transform: 'translateY(-50%)', background: 'none',
                                    border: 'none', cursor: 'pointer',
                                    color: 'var(--text-secondary)', padding: '2px', display: 'flex',
                                }}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {selected.size > 0 && (
                            <button onClick={clearAll} style={{
                                padding: '10px 16px', background: 'transparent',
                                border: '1px solid var(--border)', borderRadius: '10px',
                                color: 'var(--text-secondary)', cursor: 'pointer',
                                fontFamily: 'inherit', fontSize: '13px', fontWeight: 600,
                                whiteSpace: 'nowrap', transition: 'border-color 0.15s',
                            }}>
                                Alles abwählen
                            </button>
                        )}
                    </div>

                    {/* Fehler */}
                    {error && (
                        <div style={{
                            padding: '12px 16px', marginBottom: '20px',
                            background: 'rgba(255,80,80,0.08)', border: '1px solid #ff5050',
                            borderRadius: '8px', color: '#ff5050', fontSize: '13px',
                        }}>
                            {error}
                        </div>
                    )}
                </motion.div>

                {/* Loading */}
                {loading && (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-secondary)' }}>
                        Programmliste wird geladen...
                    </div>
                )}

                {/* Programm-Grid */}
                {!loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                        {categories.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
                                Keine Programme gefunden.
                            </div>
                        )}
                        {categories.map((cat, ci) => {
                            const progs    = grouped[cat];
                            const color    = CATEGORY_COLORS[cat] || 'var(--accent)';
                            const allSel   = progs.every(p => selected.has(p.id));
                            const someSel  = progs.some(p => selected.has(p.id));
                            return (
                                <motion.div
                                    key={cat}
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: ci * 0.04 }}
                                >
                                    {/* Kategorie-Header */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center',
                                        gap: '10px', marginBottom: '12px',
                                    }}>
                                        <div style={{ width: '3px', height: '16px', background: color, borderRadius: '2px' }} />
                                        <span style={{
                                            fontSize: '11px', fontWeight: 800,
                                            letterSpacing: '0.1em', textTransform: 'uppercase', color,
                                        }}>
                                            {cat}
                                        </span>
                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                            {progs.length}
                                        </span>
                                        <button
                                            onClick={() => toggleCategory(cat)}
                                            style={{
                                                marginLeft: 'auto', padding: '4px 14px',
                                                background: allSel ? `${color}18` : 'transparent',
                                                border: `1px solid ${someSel ? color : 'var(--border)'}`,
                                                borderRadius: '20px', cursor: 'pointer',
                                                fontSize: '11px', fontWeight: 700,
                                                fontFamily: 'inherit',
                                                color: someSel ? color : 'var(--text-secondary)',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {allSel ? 'Alle abwählen' : 'Alle wählen'}
                                        </button>
                                    </div>

                                    {/* Grid */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
                                        gap: '8px',
                                    }}>
                                        {progs.map(prog => (
                                            <ProgramCard
                                                key={prog.id}
                                                program={prog}
                                                isSelected={selected.has(prog.id)}
                                                onToggle={() => toggle(prog.id)}
                                            />
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Sticky Bottom Bar */}
            <div style={{
                position: 'fixed', bottom: 0, left: 0, right: 0,
                background: 'rgba(var(--bg-primary-rgb, 10,10,20), 0.95)',
                backdropFilter: 'blur(12px)',
                borderTop: '1px solid var(--border)',
                padding: '14px 32px',
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
                zIndex: 100,
            }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {selected.size === 0
                        ? 'Kein Programm ausgewählt'
                        : (
                            <>
                                <strong style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
                                    {selected.size}
                                </strong>
                                {' '}{selected.size === 1 ? 'Programm' : 'Programme'} ausgewählt
                            </>
                        )
                    }
                </div>

                <button
                    onClick={downloadInstaller}
                    disabled={!selected.size || downloading}
                    className="btn-primary"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        minWidth: '200px', justifyContent: 'center',
                        opacity: (!selected.size || downloading) ? 0.45 : 1,
                        cursor: (!selected.size || downloading) ? 'not-allowed' : 'pointer',
                        transition: 'opacity 0.15s',
                    }}
                >
                    {done
                        ? <><Check size={15} /> Heruntergeladen</>
                        : downloading
                        ? 'Wird generiert...'
                        : <><Download size={15} /> Installer herunterladen</>
                    }
                </button>
            </div>
        </div>
    );
}
